<script>
  import { onMount } from "svelte";
  import { loadCommandCenterManifest, validateManifestUrl } from "./command-center-manifest.mjs";
  import { signingKeyToHex } from "./command-center-signing-key.mjs";

  export let socket_query;

  let mounted = false;
  let loadGeneration = 0;
  let sessionGeneration = 0;
  let loading = true;
  let error = "";
  let message = "";
  let busy = false;
  let apps = [];
  let invalid = [];
  let groups = [];
  let editId = "";
  let draftId = "";
  let manifestUrl = "";
  let scope = "all";
  let selectedGroups = [];
  let newGroup = "";
  let preview = null;
  let previewError = "";
  let previewLoading = false;
  let previewAbort = null;
  let previewGeneration = 0;
  const pending = new Set();
  let sessionConfig = null;
  let sessionLoading = true;
  let sessionBusy = false;
  let sessionError = "";
  let sessionMessage = "";
  let sessionKeyHex = "";
  let sessionKeyFormat = "hex";
  let sessionEnabled = false;
  let issuer = "";
  let audience = "";
  let cookieName = "";
  let headerName = "";
  let cookieDomain = "";
  let ttlSeconds = 43200;

  function cancelPendingRequests() {
    for (const item of pending) {
      clearTimeout(item.timer);
      item.resolve({ ok: false, error: "Sesión modificada" });
    }
    pending.clear();
  }

  function socketRequest(event, ...args) {
    return new Promise((resolve) => {
      const item = { resolve, timer: null };
      item.timer = setTimeout(() => {
        pending.delete(item);
        resolve({ ok: false, error: "Tiempo de espera del servidor agotado" });
      }, 10000);
      pending.add(item);
      socket_query.emit(event, ...args, (response) => {
        if (!pending.has(item)) return;
        pending.delete(item);
        clearTimeout(item.timer);
        resolve(response || { ok: false, error: "Respuesta vacía del servidor" });
      });
    });
  }

  async function loadApps() {
    const generation = ++loadGeneration;
    loading = true;
    error = "";
    const result = await socketRequest("command_center_admin");
    if (!mounted || generation !== loadGeneration) return;
    loading = false;
    if (!result.ok) {
      apps = [];
      invalid = [];
      groups = [];
      error = result.error || "No fue posible consultar las aplicaciones";
      return;
    }
    apps = Array.isArray(result.apps) ? result.apps : [];
    invalid = Array.isArray(result.invalid) ? result.invalid : [];
    groups = Array.isArray(result.groups) ? result.groups : [];
  }

  async function loadSessionConfig() {
    const generation = sessionGeneration;
    sessionLoading = true;
    sessionError = "";
    const result = await socketRequest("command_center_session_admin");
    if (!mounted || generation !== sessionGeneration) return;
    sessionLoading = false;
    if (!result.ok) {
      sessionConfig = null;
      sessionError = result.error || "No fue posible consultar la sesión federada";
      return;
    }
    sessionConfig = result;
    sessionEnabled = result.enabledInSettings;
    issuer = result.settings.issuer;
    audience = result.settings.audience;
    cookieName = result.settings.cookieName;
    headerName = result.settings.headerName;
    cookieDomain = result.settings.cookieDomain;
    ttlSeconds = result.settings.ttlSeconds;
    sessionKeyHex = "";
  }

  async function saveSessionConfig() {
    if (sessionBusy || sessionLoading || !sessionConfig?.masterReady) return;
    sessionError = "";
    sessionMessage = "";
    let signingKeyHex;
    if (sessionKeyHex) {
      try {
        signingKeyHex = signingKeyToHex(sessionKeyHex, sessionKeyFormat);
      } catch (caught) {
        sessionError = caught.message;
        return;
      }
    }
    if (!Number.isInteger(Number(ttlSeconds)) || Number(ttlSeconds) < 300 || Number(ttlSeconds) > 43200) {
      sessionError = "El TTL permitido es 300–43200 segundos";
      return;
    }
    if (!window.confirm("El cambio se activa inmediatamente si el runtime está habilitado. Una clave, issuer, cookie o TTL nuevos pueden dejar de funcionar en los subportales hasta que actualicen sus validadores. ¿Aplicar ahora?")) return;
    sessionBusy = true;
    const generation = sessionGeneration;
    const result = await socketRequest("command_center_session_save", {
      version: sessionConfig.version,
      settings: { issuer, audience, cookieName, headerName, cookieDomain,
        ttlSeconds: Number(ttlSeconds) },
      enabled: sessionEnabled, signingKeyHex,
      confirm: "APLICAR CAMBIO DE SESION",
    });
    sessionKeyHex = "";
    if (!mounted || generation !== sessionGeneration) return;
    sessionBusy = false;
    if (!result.ok) {
      sessionError = result.error || "No se pudo guardar la sesión federada";
      return;
    }
    sessionConfig = result;
    sessionMessage = "Configuración aplicada. Comprueba los subportales y distribuye la nueva clave por un canal seguro.";
    loadSessionConfig();
  }

  function clearPreview() {
    previewGeneration++;
    if (previewAbort) previewAbort.abort();
    previewAbort = null;
    preview = null;
    previewError = "";
    previewLoading = false;
  }

  function startNew() {
    clearPreview();
    editId = "";
    draftId = "";
    manifestUrl = "";
    scope = "all";
    selectedGroups = [];
    newGroup = "";
    error = "";
    message = "";
  }

  function editApp(app) {
    clearPreview();
    editId = app.id;
    draftId = app.id;
    manifestUrl = app.manifestUrl;
    scope = app.groups === null ? "all" : "groups";
    selectedGroups = app.groups ? [...app.groups] : [];
    newGroup = "";
    error = "";
    message = "";
  }

  function addGroup() {
    const value = newGroup.trim();
    if (!value || value.length > 128 || /[\x00-\x1f\x7f]/.test(value)) {
      error = "Indica un identificador de grupo válido (máximo 128 caracteres)";
      return;
    }
    if (selectedGroups.length >= 50 && !selectedGroups.includes(value)) {
      error = "Una aplicación puede tener hasta 50 grupos";
      return;
    }
    if (!selectedGroups.includes(value)) selectedGroups = [...selectedGroups, value];
    newGroup = "";
    error = "";
  }

  function removeGroup(group) {
    selectedGroups = selectedGroups.filter((value) => value !== group);
  }

  async function testManifest() {
    clearPreview();
    const generation = previewGeneration;
    let url;
    try {
      url = validateManifestUrl(manifestUrl);
    } catch (caught) {
      previewError = caught.message;
      return;
    }
    previewAbort = new AbortController();
    previewLoading = true;
    try {
      const result = await loadCommandCenterManifest(url, {
        portalOrigin: window.location.origin,
        signal: previewAbort.signal,
      });
      if (mounted && generation === previewGeneration) preview = result;
    } catch (caught) {
      if (mounted && generation === previewGeneration) previewError = caught.message;
    } finally {
      if (mounted && generation === previewGeneration) previewLoading = false;
    }
  }

  async function saveApp() {
    if (busy || loading) return;
    error = "";
    message = "";
    const id = draftId.trim();
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(id) || (editId && id !== editId)) {
      error = "El ID debe contener hasta 64 letras minúsculas, números, _ o - y no se puede cambiar";
      return;
    }
    if (scope === "groups" && !selectedGroups.length) {
      error = "Selecciona al menos un grupo o cambia a Todos";
      return;
    }
    let url;
    try {
      url = validateManifestUrl(manifestUrl);
    } catch (caught) {
      error = caught.message;
      return;
    }
    busy = true;
    const generation = sessionGeneration;
    const result = await socketRequest("command_center_save", {
      id, manifestUrl: url, groups: scope === "all" ? null : selectedGroups,
    });
    if (!mounted || generation !== sessionGeneration) return;
    busy = false;
    if (!result.ok) {
      error = result.error || "No se pudo guardar la aplicación";
      return;
    }
    editId = id;
    draftId = id;
    message = "Aplicación guardada. Los portales conectados actualizarán su catálogo.";
    loadApps();
  }

  async function removeApp(id) {
    if (busy || !window.confirm(`¿Quitar la aplicación ${id} de Command Center?`)) return;
    busy = true;
    const generation = sessionGeneration;
    error = "";
    message = "";
    const result = await socketRequest("command_center_delete", id);
    if (!mounted || generation !== sessionGeneration) return;
    busy = false;
    if (!result.ok) {
      error = result.error || "No se pudo quitar la aplicación";
      return;
    }
    if (editId === id) startNew();
    message = "Aplicación eliminada.";
    loadApps();
  }

  function onDisconnected() {
    sessionGeneration++;
    cancelPendingRequests();
    loadGeneration++;
    loading = true;
    busy = false;
    apps = [];
    invalid = [];
    groups = [];
    sessionConfig = null;
    sessionKeyHex = "";
    sessionLoading = true;
    sessionBusy = false;
    sessionMessage = "";
    message = "";
    clearPreview();
  }

  function onUserInfo(data) {
    onDisconnected();
    if (data) { loadApps(); loadSessionConfig(); }
  }

  onMount(() => {
    mounted = true;
    socket_query.on("command_center_changed", loadApps);
    socket_query.on("command_center_session_changed", loadSessionConfig);
    socket_query.on("disconnect", onDisconnected);
    socket_query.on("user_info", onUserInfo);
    loadApps();
    loadSessionConfig();
    return () => {
      mounted = false;
      loadGeneration++;
      clearPreview();
      socket_query.off("command_center_changed", loadApps);
      socket_query.off("command_center_session_changed", loadSessionConfig);
      socket_query.off("disconnect", onDisconnected);
      socket_query.off("user_info", onUserInfo);
      cancelPendingRequests();
    };
  });
</script>

<section class="cc-settings">
  <div class="cc-settings__title">
    <div>
      <h2>Aplicaciones de Command Center</h2>
      <p>Catálogo global de aplicaciones externas. Los grupos son grupos de acceso de usuarios, no grupos de unidades.</p>
    </div>
    <button type="button" class="btn btn-outline-info" on:click={startNew} disabled={busy}>Nueva app</button>
  </div>
  {#if error}<div class="alert alert-danger" role="alert">{error}</div>{/if}
  {#if message}<div class="alert alert-success" role="status">{message}</div>{/if}
  {#if loading}
    <p role="status">Consultando catálogo…</p>
  {:else}
    <div class="cc-settings__grid">
      <div class="cc-settings__list">
        <h3>Apps registradas <span>{apps.length}</span></h3>
        {#if !apps.length && !invalid.length}
          <p>No hay apps registradas. Agrega una o dales de alta manualmente en Redis.</p>
        {/if}
        {#each apps as app (app.id)}
          <article class:active={editId === app.id} class="cc-settings__item">
            <div>
              <strong>{app.id}</strong>
              <small>{app.groups === null ? "Todos los grupos" : app.groups.join(", ")}</small>
              <code>{app.manifestUrl}</code>
            </div>
            <div class="cc-settings__actions">
              <button type="button" class="btn btn-sm btn-outline-primary" on:click={() => editApp(app)} disabled={busy}>Editar</button>
              <button type="button" class="btn btn-sm btn-outline-danger" on:click={() => removeApp(app.id)} disabled={busy}>Quitar</button>
            </div>
          </article>
        {/each}
        {#if invalid.length}
          <div class="cc-settings__invalid" role="alert">
            <strong>Entradas Redis inválidas</strong>
            <p>Se ocultan de los usuarios hasta corregirlas. Las que tengan un ID inválido se eliminan manualmente con HDEL.</p>
            {#each invalid as item}
              <div class="cc-settings__invalid-item">
                <span>{item.id}: {item.error}</span>
                {#if /^[a-z0-9][a-z0-9_-]{0,63}$/.test(item.id)}
                  <button type="button" class="btn btn-sm btn-outline-danger" on:click={() => removeApp(item.id)} disabled={busy}>Quitar</button>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <form class="cc-settings__editor" on:submit|preventDefault={saveApp}>
        <h3>{editId ? "Editar app" : "Registrar app"}</h3>
        <div class="form-group">
          <label for="cc-app-id">ID estable de la app</label>
          <input id="cc-app-id" type="text" class="form-control" bind:value={draftId}
            placeholder="centro-flota" maxlength="64" required disabled={!!editId || busy} />
          <small>Solo minúsculas, números, guiones y _. Para cambiarlo, crea una app nueva.</small>
        </div>
        <div class="form-group">
          <label for="cc-app-manifest">URL HTTPS del manifiesto PWA</label>
          <input id="cc-app-manifest" type="url" class="form-control" bind:value={manifestUrl}
            on:input={clearPreview} placeholder="https://apps.example.com/manifest.json"
            maxlength="2048" required disabled={busy} />
          <button type="button" class="btn btn-sm btn-outline-secondary mt-2"
            on:click={testManifest} disabled={!manifestUrl || previewLoading || busy}>
            {previewLoading ? "Consultando…" : "Probar manifiesto"}
          </button>
        </div>
        {#if preview}
          <div class="cc-settings__preview" role="status">
            <img src={preview.iconUrl} alt="" referrerpolicy="no-referrer" on:error={(event) => { event.currentTarget.style.display = "none"; }} />
            <div><strong>{preview.name}</strong><small>Inicio: {preview.startUrl}</small></div>
          </div>
        {/if}
        {#if previewError}<div class="alert alert-warning" role="alert">{previewError}</div>{/if}
        <fieldset class="cc-settings__scope">
          <legend>Disponible para</legend>
          <label><input type="radio" bind:group={scope} value="all" disabled={busy} /> Todos (predeterminado)</label>
          <label><input type="radio" bind:group={scope} value="groups" disabled={busy} /> Solo grupos seleccionados</label>
        </fieldset>
        {#if scope === "groups"}
          <div class="cc-settings__chips">
            {#each selectedGroups as group (group)}
              <span>{group}<button type="button" aria-label={`Quitar grupo ${group}`}
                on:click={() => removeGroup(group)} disabled={busy}>×</button></span>
            {/each}
          </div>
          <div class="cc-settings__group-entry">
            <input type="text" class="form-control" bind:value={newGroup} list="cc-tenant-groups"
              placeholder="Escribe o elige un grupo de acceso" maxlength="128" disabled={busy}
              on:keydown={(event) => { if (event.key === "Enter") { event.preventDefault(); addGroup(); } }} />
            <datalist id="cc-tenant-groups">
              {#each groups.filter((group) => !selectedGroups.includes(group)) as group}
                <option value={group} />
              {/each}
            </datalist>
            <button type="button" class="btn btn-outline-info" on:click={addGroup} disabled={busy}>Agregar grupo</button>
          </div>
          {#if !selectedGroups.length}<small class="text-warning">Agrega al menos un grupo antes de guardar.</small>{/if}
        {/if}
        <div class="cc-settings__footer">
          <button type="submit" class="btn btn-primary" disabled={busy || loading || (scope === "groups" && !selectedGroups.length)}>
            {busy ? "Guardando…" : "Guardar app"}
          </button>
          <p>La vista previa requiere CORS. La app debe permitir iframe y enviar <strong>command-center:ready</strong>. Si la sesión federada está activada, recibirá JWT y llave de cabecera; nunca la clave de firma ni la cookie HttpOnly.</p>
        </div>
      </form>
    </div>
  {/if}
  <form class="cc-settings__session" on:submit|preventDefault={saveSessionConfig}>
    <h3>Sesión federada de Command Center</h3>
    <p>Complementa el login actual de Vector. Una única sesión para todas las apps autorizadas; no autentica automáticamente a sus backends.</p>
    {#if sessionLoading}
      <p role="status">Consultando configuración…</p>
    {:else if sessionConfig}
      <p class="cc-settings__session-meta">Estado: <strong>{sessionConfig.enabled ? "Emitiendo" : "Inactiva"}</strong>
        · versión {sessionConfig.version}
        · huella de clave {sessionConfig.keyFingerprint || "Sin clave"}</p>
      {#if !sessionConfig.masterReady}
        <div class="alert alert-warning" role="alert">Falta la clave maestra runtime. Provisiona el archivo de secretos y recrea websocket antes de guardar.</div>
      {:else if !sessionConfig.runtimeEnabled}
        <div class="alert alert-warning" role="status">La emisión está bloqueada por COMMAND_CENTER_SESSION_ENABLED=0. Actívala únicamente después de validar la seguridad de iframe/service worker y distribuir la clave real.</div>
      {/if}
      {#if sessionConfig.previousUntil}<small>Clave/configuración anterior aceptada por Vector hasta {sessionConfig.previousUntil}; esto no actualiza los subportales.</small>{/if}
      <div class="cc-settings__session-grid">
        <label>Issuer HTTPS del portal
          <input class="form-control" type="url" bind:value={issuer} maxlength="256" required disabled={sessionBusy} /></label>
        <label>Audience
          <input class="form-control" type="text" bind:value={audience} maxlength="128" required disabled={sessionBusy} /></label>
        <label>Cookie HttpOnly (Domain del sitio)
          <input class="form-control" type="text" bind:value={cookieName} maxlength="64" required disabled={sessionBusy} /></label>
        <label>Cabecera derivada (X-Session-Key)
          <input class="form-control" type="text" bind:value={headerName} maxlength="64" required disabled={sessionBusy} /></label>
        <label>Dominio de la cookie
          <input class="form-control" type="text" bind:value={cookieDomain} maxlength="253" required disabled={sessionBusy} /></label>
        <label>TTL en segundos (300–43200)
          <input class="form-control" type="number" min="300" max="43200" step="1" bind:value={ttlSeconds} required disabled={sessionBusy} /></label>
      </div>
      {#if Number(ttlSeconds) !== 43200}
        <p class="text-warning" role="alert">Un TTL distinto de 43200 no cumple el contrato literal v1: actualiza los verificadores externos antes de activarlo.</p>
      {/if}
      <label class="cc-settings__session-key" for="cc-signing-key-format">Formato de clave HS256 nueva
        <select id="cc-signing-key-format" class="form-control" bind:value={sessionKeyFormat}
          on:change={() => { sessionKeyHex = ""; }} disabled={sessionBusy}>
          <option value="hex">Hexadecimal (bytes existentes)</option>
          <option value="utf8">Texto UTF-8 (convertir a bytes)</option>
        </select>
      </label>
      <label class="cc-settings__session-key" for="cc-signing-key">Clave HS256 nueva (solo escritura; deja vacío para conservarla)
        <input id="cc-signing-key" class="form-control" type="password" autocomplete="new-password" bind:value={sessionKeyHex}
          maxlength={sessionKeyFormat === "hex" ? 2048 : 1024}
          placeholder="No se puede recuperar después de guardar" disabled={sessionBusy} /></label>
      <small>32–1024 bytes de clave privada. Si usas texto, los subportales deben convertirlo a bytes UTF-8 y configurar el hex resultante. Se rechazan claves débiles o públicas; rotar invalida JWT nuevos en subportales con la clave antigua.</small>
      <label class="cc-settings__session-enabled"><input type="checkbox" bind:checked={sessionEnabled} disabled={sessionBusy} /> Habilitar emisión cuando el runtime lo permita</label>
      {#if sessionError}<div class="alert alert-danger" role="alert">{sessionError}</div>{/if}
      {#if sessionMessage}<div class="alert alert-success" role="status">{sessionMessage}</div>{/if}
      <button type="submit" class="btn btn-primary" disabled={sessionBusy || !sessionConfig.masterReady}>
        {sessionBusy ? "Aplicando…" : "Guardar configuración de sesión"}
      </button>
    {:else if sessionError}<div class="alert alert-danger" role="alert">{sessionError}</div>{/if}
  </form>
</section>

<style>
  .cc-settings { width: 100%; padding: 1.5rem; }
  .cc-settings__title { display: flex; justify-content: space-between; align-items: start; gap: 1rem; }
  .cc-settings h2 { font-size: 1.4rem; color: #17324d; }
  .cc-settings h3 { font-size: 1.1rem; color: #254665; margin-bottom: 1rem; }
  .cc-settings p { color: #526b7d; }
  .cc-settings__grid { display: grid; grid-template-columns: minmax(15rem, 0.9fr) minmax(22rem, 1.1fr); gap: 1.2rem; }
  .cc-settings__list, .cc-settings__editor { background: #fff; border: 1px solid #e0e9f0; border-radius: 0.8rem; padding: 1.1rem; min-width: 0; }
  .cc-settings__list h3 span { color: #718392; font-size: 0.8rem; }
  .cc-settings__item { border-top: 1px solid #e6edf2; padding: 0.9rem 0; }
  .cc-settings__item.active { border-left: 3px solid #3b82f6; padding-left: 0.65rem; }
  .cc-settings__item small, .cc-settings__item code { display: block; overflow-wrap: anywhere; }
  .cc-settings__item small { color: #526b7d; }
  .cc-settings__item code { font-size: 0.75rem; color: #375973; }
  .cc-settings__actions { display: flex; gap: 0.5rem; margin-top: 0.6rem; }
  .cc-settings__invalid { margin-top: 1rem; padding: 0.8rem; background: #fff4e4; border-radius: 0.5rem; }
  .cc-settings__invalid p { font-size: 0.8rem; }
  .cc-settings__invalid-item { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin: 0.4rem 0; overflow-wrap: anywhere; }
  .cc-settings__preview { display: flex; gap: 0.7rem; align-items: center; padding: 0.8rem; border-radius: 0.5rem; background: #edf7f5; margin-bottom: 1rem; }
  .cc-settings__preview img { width: 2.3rem; height: 2.3rem; object-fit: contain; }
  .cc-settings__preview small { display: block; overflow-wrap: anywhere; }
  .cc-settings__scope { border: 0; padding: 0; margin-bottom: 1rem; }
  .cc-settings__scope legend { font-size: 0.95rem; font-weight: 600; }
  .cc-settings__scope label { display: block; margin: 0.25rem 0; }
  .cc-settings__scope input { margin-right: 0.4rem; }
  .cc-settings__chips { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.6rem; }
  .cc-settings__chips span { background: #e7f1fb; border-radius: 1rem; padding: 0.2rem 0.6rem; }
  .cc-settings__chips button { border: 0; background: transparent; color: #33577b; font-weight: 700; margin-left: 0.3rem; }
  .cc-settings__group-entry { display: flex; gap: 0.5rem; }
  .cc-settings__footer { border-top: 1px solid #e6edf2; padding-top: 1rem; margin-top: 1rem; }
  .cc-settings__footer p { font-size: 0.8rem; margin: 0.8rem 0 0; }
  .cc-settings__session { background: #fff; border: 1px solid #cddfec; border-radius: 0.8rem; margin-top: 1.4rem; padding: 1.2rem; }
  .cc-settings__session-grid { display: grid; grid-template-columns: repeat(2, minmax(12rem, 1fr)); gap: 0.9rem; margin: 1rem 0; }
  .cc-settings__session-grid label, .cc-settings__session-key { display: block; color: #254665; font-size: 0.9rem; }
  .cc-settings__session-key { margin: 1rem 0; }
  .cc-settings__session-meta { background: #eef5fc; padding: 0.6rem; border-radius: 0.4rem; }
  .cc-settings__session-enabled { display: block; margin: 1rem 0; }
  @media (max-width: 1100px) { .cc-settings__grid { grid-template-columns: 1fr; } }
  @media (max-width: 700px) { .cc-settings__session-grid { grid-template-columns: 1fr; } }
</style>